// Deterministic models of how a language model handles text, for Lesson 3.1 ("A Model
// Predicts Text, It Does Not Know Maya's Tasks"). They simulate tokens, the next-token
// choice, what a model knows and the context window. They never call a model.

/** Example rates used throughout Module 3, labelled "example rate" in every lesson. */
export const EXAMPLE_INPUT_PRICE = 3.0;
export const EXAMPLE_OUTPUT_PRICE = 15.0;

export type NoteId = 1 | 2 | 3;
export interface LessonNote {
  id: NoteId;
  title: string;
  text: string;
}
/** The first three notes of the module's shared dataset (module-3/alignment.md). */
export const lessonNotes: readonly [LessonNote, LessonNote, LessonNote] = [
  {
    id: 1,
    title: 'Apollo standup, 3 March',
    text: 'Apollo standup, 3 March. Demo is on the 12th. We still need a room for it. Ravi asked for the agenda by Friday. Jo thinks the projector in the small room is broken, someone should check it. Badges are printed.',
  },
  {
    id: 2,
    title: 'Venue email',
    text: 'From: bookings@riverside-hall.example. Subject: Your booking on 12 March. Hello Maya, thanks for your booking. Please send the final headcount by 8 March. Could you also tell us whether you need a microphone? Regards, Priya',
  },
  {
    id: 3,
    title: 'Thank-you note',
    text: 'Thanks everyone for a great week. The release note went out and the feedback was good. Enjoy the weekend!',
  },
];
export const noteById = (id: NoteId): LessonNote =>
  lessonNotes.find((note) => note.id === id) ?? lessonNotes[0];

// --- model-tokens: a toy tokenizer -------------------------------------------------------

const ENDINGS = ['tion', 'ing', 'ed', 'er', 'or', 'es', 'ly', 's'];
const PUNCTUATION = /[.,;:!?()"'@]/;

/** Splits one run of letters the way the toy tokenizer does. */
function splitWord(word: string): string[] {
  if (word.length <= 6) return [word];
  for (const ending of ENDINGS) {
    if (word.length - ending.length >= 4 && word.toLowerCase().endsWith(ending))
      return [word.slice(0, word.length - ending.length), word.slice(word.length - ending.length)];
  }
  const half = Math.ceil(word.length / 2);
  return [word.slice(0, half), word.slice(half)];
}

/**
 * The toy tokenizer: spaces separate chunks; punctuation and digit runs are pieces of
 * their own; words of up to six letters stay whole; longer words split before a common
 * ending when at least four letters remain, otherwise in half. Real tokenizers learn
 * their pieces from data, so their splits differ; the counting lesson is the same.
 */
export function tokenize(text: string): string[] {
  const pieces: string[] = [];
  for (const chunk of text.split(/\s+/).filter(Boolean)) {
    let letters = '';
    let digits = '';
    const flush = () => {
      if (letters) pieces.push(...splitWord(letters));
      if (digits) pieces.push(digits);
      letters = '';
      digits = '';
    };
    for (const character of chunk) {
      if (PUNCTUATION.test(character) || character === '-') {
        flush();
        pieces.push(character);
      } else if (/\d/.test(character)) {
        if (letters) flush();
        digits += character;
      } else {
        if (digits) flush();
        letters += character;
      }
    }
    flush();
  }
  return pieces;
}
export const countWords = (text: string) => text.split(/\s+/).filter(Boolean).length;
/** Cost of sending `tokens` input tokens at the example rate, as a fixed-point string. */
export function inputCost(tokens: number) {
  return ((tokens / 1_000_000) * EXAMPLE_INPUT_PRICE).toFixed(6);
}

export type TokenSource = NoteId | 'own';
export interface TokenState {
  source: TokenSource;
  phrase: string;
  /** Whether the display counts words (the first idea) or tokens (the refinement). */
  countTokens: boolean;
}
export const OWN_PHRASE = 'Check the projector in the small room';
export const initialTokens = (): TokenState => ({
  source: 1,
  phrase: OWN_PHRASE,
  countTokens: false,
});
export const tokenText = (state: TokenState) =>
  state.source === 'own' ? state.phrase : noteById(state.source).text;
export function tokenSummary(state: TokenState) {
  const text = tokenText(state);
  const words = countWords(text);
  const pieces = tokenize(text);
  if (!text.trim()) return 'Type a phrase to see its pieces.';
  if (!state.countTokens)
    return `${words} words, as a person counts. Press Count tokens to see what the model counts.`;
  return `${pieces.length} tokens for ${words} words. Long words split, and punctuation counts.`;
}

// --- model-sampling: the next token is a choice among chances ----------------------------

export const SAMPLING_PREFIX = "Maya's next task is to";
export interface Candidate {
  token: string;
  weight: number;
  continuation: string;
}
export const candidates: readonly [Candidate, Candidate, Candidate, Candidate, Candidate] = [
  { token: 'book', weight: 0.4, continuation: 'book the demo room.' },
  { token: 'send', weight: 0.25, continuation: 'send the agenda to Ravi.' },
  { token: 'check', weight: 0.2, continuation: 'check the projector.' },
  { token: 'write', weight: 0.1, continuation: 'write the release note.' },
  { token: 'cancel', weight: 0.05, continuation: 'cancel the catering.' },
];
export type Temperature = 'low' | 'medium' | 'high';
export const temperatureLabels: Record<Temperature, string> = {
  low: 'Low: always the most likely',
  medium: 'Medium: by the chances shown',
  high: 'High: flatter chances',
};
/** The probability of each candidate at a temperature, rounded to whole percents that sum to 100. */
export function distribution(temperature: Temperature): { token: string; percent: number }[] {
  if (temperature === 'low')
    return candidates.map((candidate, index) => ({
      token: candidate.token,
      percent: index === 0 ? 100 : 0,
    }));
  const raw = candidates.map((candidate) =>
    temperature === 'medium' ? candidate.weight : Math.sqrt(candidate.weight),
  );
  const total = raw.reduce((sum, value) => sum + value, 0);
  const percents = raw.map((value) => Math.round((value / total) * 100));
  const drift = 100 - percents.reduce((sum, value) => sum + value, 0);
  percents[0] = (percents[0] ?? 0) + drift;
  return candidates.map((candidate, index) => ({
    token: candidate.token,
    percent: percents[index] ?? 0,
  }));
}
/** Six fixed draws in [0, 1), used in order, so every learner sees the same picks. */
export const DRAWS = [0.62, 0.17, 0.91, 0.35, 0.74, 0.08] as const;
export const drawFor = (run: number) => DRAWS[(run - 1) % DRAWS.length] ?? 0;
/** The candidate whose cumulative chance first exceeds the draw. */
export function pickFor(temperature: Temperature, run: number): Candidate {
  const chances = distribution(temperature);
  const draw = drawFor(run) * 100;
  let cumulative = 0;
  for (const [index, chance] of chances.entries()) {
    cumulative += chance.percent;
    if (draw < cumulative) return candidates[index] ?? candidates[0];
  }
  return candidates[0];
}
export interface Pick {
  run: number;
  draw: number;
  token: string;
  continuation: string;
}
export interface SamplingState {
  temperature: Temperature;
  picks: Pick[];
  message: string;
}
export const initialSampling = (): SamplingState => ({
  temperature: 'medium',
  picks: [],
  message: 'No runs yet. Press Pick the next token.',
});
export type SamplingAction =
  { type: 'temperature'; value: Temperature } | { type: 'pick' } | { type: 'reset' };
export function samplingTransition(state: SamplingState, action: SamplingAction): SamplingState {
  switch (action.type) {
    case 'reset':
      return initialSampling();
    case 'temperature':
      return {
        temperature: action.value,
        picks: [],
        message:
          action.value === 'low'
            ? 'Low: the most likely token wins every run. Press Pick the next token.'
            : action.value === 'high'
              ? 'High: rare tokens are picked more often. Press Pick the next token.'
              : 'Medium: tokens are picked by the chances shown. Press Pick the next token.',
      };
    case 'pick': {
      const run = state.picks.length + 1;
      const candidate = pickFor(state.temperature, run);
      const pick = {
        run,
        draw: drawFor(run),
        token: candidate.token,
        continuation: candidate.continuation,
      };
      const picks = [...state.picks, pick].slice(-6);
      const tokens = new Set(picks.map((item) => item.token));
      const message =
        state.temperature === 'low'
          ? `Run ${run} picked "${candidate.token}". At Low, every run gets the same ending.`
          : tokens.size > 1
            ? `Run ${run} picked "${candidate.token}". ${picks.length} runs have ended ${tokens.size} different ways.`
            : `Run ${run} picked "${candidate.token}". Pick again to see whether the ending changes.`;
      return { ...state, picks, message };
    }
  }
}

// --- model-knowledge: the model learned from text, not from CollabBoard ------------------

export type KnowledgeQuestion = 'task-9-owner' | 'todays-date';
export const knowledgeQuestions: Record<KnowledgeQuestion, string> = {
  'task-9-owner': 'Who is working on task 9?',
  'todays-date': "What is today's date?",
};
export interface KnowledgeState {
  question: KnowledgeQuestion;
  includeTasks: boolean;
  includeDate: boolean;
}
export const initialKnowledge = (): KnowledgeState => ({
  question: 'task-9-owner',
  includeTasks: false,
  includeDate: false,
});
export const TASK_LIST = [
  'Tasks in Apollo:',
  '9 Write the release note (Ravi)',
  '10 Check the dates (unassigned)',
];
export const TODAY = 'Today is 3 March 2026.';
/** The prompt the model would receive: our instructions plus whatever we chose to include. */
export function knowledgePrompt(state: KnowledgeState): string[] {
  return [
    'system: Answer in one sentence.',
    ...(state.includeTasks ? TASK_LIST.map((line) => `system: ${line}`) : []),
    ...(state.includeDate ? [`system: ${TODAY}`] : []),
    `user: ${knowledgeQuestions[state.question]}`,
  ];
}
export interface KnowledgeAnswer {
  text: string;
  source: 'guess' | 'from the prompt';
  explanation: string;
}
export function knowledgeAnswer(state: KnowledgeState): KnowledgeAnswer {
  if (state.question === 'task-9-owner') {
    return state.includeTasks
      ? {
          text: 'Task 9, Write the release note, is assigned to Ravi.',
          source: 'from the prompt',
          explanation: 'The task list is in the prompt, so the answer repeats it.',
        }
      : {
          text: 'Task 9 is assigned to Jo.',
          source: 'guess',
          explanation:
            'The prompt does not name task 9. The model guessed, and the guess is wrong.',
        };
  }
  return state.includeDate
    ? {
        text: 'Today is 3 March 2026.',
        source: 'from the prompt',
        explanation: 'The date is in the prompt, so the answer repeats it.',
      }
    : {
        text: 'Today is 14 June 2025.',
        source: 'guess',
        explanation:
          'The model has no clock. It guessed a date that looked likely in its training text.',
      };
}

// --- model-context: what we send is all the model knows ----------------------------------

export const WINDOW_SIZES = [60, 120, 240] as const;
export type WindowSize = (typeof WINDOW_SIZES)[number];
export interface ContextState {
  window: WindowSize;
  /** Notes in the order they were added. */
  added: NoteId[];
  message: string;
}
export const initialContext = (): ContextState => ({
  window: 60,
  added: [],
  message: 'The window is empty. Press Add note 1.',
});
export const noteTokens = (id: NoteId) => tokenize(noteById(id).text).length;
export interface ContextFit {
  inside: NoteId[];
  dropped: NoteId[];
  used: number;
}
/** Oldest notes fall out first until the rest fits the window. */
export function contextFit(state: ContextState): ContextFit {
  let inside = [...state.added];
  const dropped: NoteId[] = [];
  let used = inside.reduce((sum, id) => sum + noteTokens(id), 0);
  while (used > state.window && inside.length) {
    const [oldest, ...rest] = inside;
    if (oldest === undefined) break;
    dropped.push(oldest);
    inside = rest;
    used -= noteTokens(oldest);
  }
  return { inside, dropped, used };
}
export const CONTEXT_QUESTION = 'What did note 1 say about the agenda?';
export function contextAnswer(state: ContextState): KnowledgeAnswer {
  const fit = contextFit(state);
  if (fit.inside.includes(1))
    return {
      text: 'Ravi asked for the agenda by Friday.',
      source: 'from the prompt',
      explanation: 'Note 1 is inside the window, so the answer comes from it.',
    };
  return {
    text: 'The agenda is due on Monday.',
    source: 'guess',
    explanation: fit.dropped.includes(1)
      ? 'The model never saw note 1, so it guessed.'
      : 'Note 1 is not in the window, so the model guessed.',
  };
}
export type ContextAction =
  { type: 'window'; value: WindowSize } | { type: 'add'; note: NoteId } | { type: 'reset' };
export function contextTransition(state: ContextState, action: ContextAction): ContextState {
  switch (action.type) {
    case 'reset':
      return initialContext();
    case 'window': {
      const next = { ...state, window: action.value };
      const fit = contextFit(next);
      return {
        ...next,
        message: fit.dropped.length
          ? `At ${action.value} tokens, note${fit.dropped.length === 1 ? '' : 's'} ${fit.dropped.join(' and ')} fell out.`
          : `At ${action.value} tokens, all added notes fit (${fit.used} tokens used).`,
      };
    }
    case 'add': {
      if (state.added.includes(action.note))
        return { ...state, message: `Note ${action.note} is already in the window.` };
      const next = { ...state, added: [...state.added, action.note] };
      const fit = contextFit(next);
      return {
        ...next,
        message: fit.dropped.length
          ? `Note ${action.note} added, so note${fit.dropped.length === 1 ? '' : 's'} ${fit.dropped.join(' and ')} fell out of the ${state.window}-token window.`
          : `Note ${action.note} added. ${fit.used} of ${state.window} tokens used.`,
      };
    }
  }
}
